import type { ActedForView } from '@myrewards/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { actingForKeys, doneForMeQuery } from '../api/actingFor';
import { keepIdsWhole } from '../shell/Hero';
import {
  Button,
  ButtonLink,
  Card,
  EmptyState,
  formatCount,
  StatusPill,
  useToast,
  type CardHelpContent,
} from '../ui';
import { DONE_FOR_YOU_HREF, doneHref, doneLine, questionedWords } from './doneWords';
import { MarkSettled, marksSettled } from './MarkSettled';
import { Pair } from './parts';
import { QuestionDialog } from './QuestionDialog';
import { useQuestionAddress } from './useQuestionAddress';
import h from './HomeCards.module.css';

// APP-148 D2, D6: what the Stock Admin team did for an Executive or a Sector Lead, as they read
// it: on their Home for the last 30 days (the ExecHome and LeadHome boards), and every page of it
// at `/done-for-you`. Each row opens what was done and offers "Not what you asked?".

/** Home's window: the last 30 days. */
export const HOME_DAYS = 30;
/** Home shows the newest few; See them all has the rest. */
const HOME_ROWS = 5;

/** What the intro and the empty state say, on Home and the page alike. */
export const DONE_INTRO =
  'What the Stock Admin team did when you asked them to, newest first, with their note and any files. If something isn’t what you asked for, say so and the award is held.';

export const DONE_HELP: CardHelpContent = {
  shows:
    'What the Stock Admin team did for you when you asked them to: who did it, when, their note and any files. Newest first.',
  canDo:
    'Open one to see the award or the change. If it isn’t what you asked for, choose Not what you asked?: the award is held before its next step, and the System Admins and the Stock Admin team are told.',
};

/** The award's number, or what was done, to tell one row's buttons from the next. */
const subjectOf = (view: ActedForView): string => view.award?.displayId ?? view.line;

/** One thing done for the reader: who did it for them, what and when, and what they can do. */
export function DoneForYouRow({
  view,
  onQuestion,
  settling,
}: {
  view: ActedForView;
  onQuestion: (view: ActedForView) => void;
  /**
   * R92, R127: a question that held nothing is marked settled here by whoever settles it, on the
   * Done for you page and on Home's card alike.
   */
  settling?: { meId: string; previewing: boolean };
}): ReactElement {
  const status = questionedWords(view);
  const subject = subjectOf(view);
  return (
    <li className={h.row}>
      <Pair actor={view.actor.name} person={view.for.name} />
      <div className={h.rowText}>
        <h3 className={h.rowTitle}>{keepIdsWhole(view.line)}</h3>
        <p className={h.rowLine}>{doneLine(view)}</p>
      </div>
      <div className={h.rowActions}>
        {status !== null && <StatusPill tone={status.tone} size="sm" label={status.label} />}
        <ButtonLink href={doneHref(view)} variant="ghost" size="sm" aria-label={`Open ${subject}`}>
          Open
        </ButtonLink>
        {view.can.question && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Not what you asked? ${subject}`}
            onPress={() => {
              onQuestion(view);
            }}
          >
            Not what you asked?
          </Button>
        )}
        {settling !== undefined && marksSettled(view, settling.previewing) && (
          <MarkSettled view={view} meId={settling.meId} />
        )}
      </div>
    </li>
  );
}

/**
 * What follows a question: it says what happened, and the lists, Home's Waiting on you (a held
 * award may be theirs to settle) and the record read again.
 */
export function useAsked(): (asked: ActedForView) => void {
  const queryClient = useQueryClient();
  const toast = useToast();
  return (asked) => {
    toast.show({
      tone: 'done',
      title: asked.held ? 'Held, and the team is told' : 'The team is told',
      body: asked.held
        ? 'It stops before its next step until it’s settled. The System Admins and the Stock Admin team have a notice.'
        : 'The System Admins and the Stock Admin team have a notice.',
    });
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: actingForKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['home'] }),
    ]);
  };
}

/**
 * "Not what you asked?" for Home's rows (R136): in the address, as on Done for you's page and the
 * record (R89, R117), so opened from a row it's a step of its own and Back closes it. It opens
 * for one of the card's own rows; any other id in the address is left for the page that has it.
 */
export function useQuestion(rows: readonly ActedForView[]): {
  ask: (view: ActedForView) => void;
  dialog: ReactElement;
} {
  const address = useQuestionAddress();
  const asked = useAsked();
  const asking = rows.find((view) => view.id === address.questionId) ?? null;
  return {
    ask: (view) => {
      address.open(view.id);
    },
    dialog: (
      <QuestionDialog
        view={asking}
        onClose={address.close}
        onAsked={(view) => {
          address.close();
          asked(view);
        }}
      />
    ),
  };
}

/**
 * Done for you · last 30 days, on an Executive's or Sector Lead's Home (D2): the newest few, with
 * how many there are and See them all, each with Mark it settled where the page has it (R127).
 * Nobody has acted for them: the Empty board's state. Home draws it live only, never previewing.
 */
export function DoneForYouCard({ meId }: { meId: string }): ReactElement {
  const done = useQuery({ ...doneForMeQuery({ days: HOME_DAYS }), retry: false });
  const items = done.data?.items ?? [];
  const question = useQuestion(items);
  const total = done.data?.page.total ?? 0;
  const shown = items.slice(0, HOME_ROWS);
  return (
    <Card
      title="Done for you · last 30 days"
      accent="cyan"
      helpAnchor="done-for-you"
      help={DONE_HELP}
      {...(total > 0 && { badge: <StatusPill tone="info" label={formatCount(total)} /> })}
      {...(total > 0 && {
        aside: (
          <ButtonLink href={DONE_FOR_YOU_HREF} variant="ghost" size="sm">
            See them all
          </ButtonLink>
        ),
      })}
    >
      {done.isError ? (
        <div className={h.failed}>
          <p className={h.intro} role="alert">
            We couldn’t read what was done for you just now. Every row is on its own page.
          </p>
          <ButtonLink href={DONE_FOR_YOU_HREF} variant="ghost" size="sm">
            See them all
          </ButtonLink>
        </div>
      ) : shown.length === 0 ? (
        done.isPending ? (
          <p className={h.intro}>Reading what was done for you…</p>
        ) : (
          <EmptyState
            variant="first-day"
            art="shield"
            size="sm"
            headline="Nobody has acted for you in the last 30 days"
            body="When you ask the Stock Admin team to do something for you, it shows here with their note and any files. You’re told each time by bell and email."
          />
        )
      ) : (
        <>
          <p className={h.intro}>{DONE_INTRO}</p>
          <ul className={h.list}>
            {shown.map((view) => (
              <DoneForYouRow
                key={view.id}
                view={view}
                onQuestion={question.ask}
                settling={{ meId, previewing: false }}
              />
            ))}
          </ul>
          {total > shown.length && (
            <p className={h.more}>
              The newest {formatCount(shown.length)} of {formatCount(total)}. See them all for the
              rest.
            </p>
          )}
        </>
      )}
      {question.dialog}
    </Card>
  );
}
